import axios from 'axios';
import { AuthorItem, ItemSummary, ItemFilters, PaginationParams, PaginatedResponse } from '../types/item';
import { API_ENDPOINTS } from '../config/apiConfig';
import { installExpiryHandler } from '../config/sessionExpiry';

const itemApi = axios.create({
  baseURL: API_ENDPOINTS.learningApi,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
itemApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});


installExpiryHandler(itemApi);

// Grade 1 Math Mock Questions for demonstration
const GRADE_1_MATH_QUESTIONS: AuthorItem[] = [
  {
    id: 'grade1-math-001',
    question: '<p>What is 2 + 3?</p>',
    options: ['4', '5', '6', '7'],
    answerIndex: 1,
    correctAnswer: '5',
    explanation: '<p>When we add 2 + 3, we count: 2, then 3 more makes 5!</p>',
    tags: ['addition', 'basic-math', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-15'),
    updatedAt: new Date('2024-01-15'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-002',
    question: '<p>Count the apples: 🍎🍎🍎🍎 How many apples are there?</p>',
    options: ['3', '4', '5', '6'],
    answerIndex: 1,
    correctAnswer: '4',
    explanation: '<p>Let\'s count together: 1, 2, 3, 4 apples!</p>',
    tags: ['counting', 'numbers', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-16'),
    updatedAt: new Date('2024-01-16'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-003',
    question: '<p>What comes after 7?</p>',
    options: ['6', '8', '9', '10'],
    answerIndex: 1,
    correctAnswer: '8',
    explanation: '<p>The number sequence is: 6, 7, 8, 9, 10. So 8 comes after 7!</p>',
    tags: ['number-sequence', 'counting', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-17'),
    updatedAt: new Date('2024-01-17'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-004',
    question: '<p>If you have 5 toys and give away 2 toys, how many toys do you have left?</p>',
    options: ['2', '3', '4', '7'],
    answerIndex: 1,
    correctAnswer: '3',
    explanation: '<p>Start with 5 toys. Take away 2 toys: 5 - 2 = 3 toys left!</p>',
    tags: ['subtraction', 'word-problems', 'grade-1'],
    score: 2,
    duration: 45,
    createdAt: new Date('2024-01-18'),
    updatedAt: new Date('2024-01-18'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-005',
    question: '<p>Which shape has 3 sides?</p>',
    options: ['Circle', 'Square', 'Triangle', 'Rectangle'],
    answerIndex: 2,
    correctAnswer: 'Triangle',
    explanation: '<p>A triangle has exactly 3 sides and 3 corners!</p>',
    tags: ['shapes', 'geometry', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-19'),
    updatedAt: new Date('2024-01-19'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-006',
    question: '<p>What is 10 - 1?</p>',
    options: ['8', '9', '10', '11'],
    answerIndex: 1,
    correctAnswer: '9',
    explanation: '<p>When we take 1 away from 10, we get 9!</p>',
    tags: ['subtraction', 'basic-math', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-20'),
    updatedAt: new Date('2024-01-20'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-007',
    question: '<p>How many fingers do you have on both hands?</p>',
    options: ['8', '9', '10', '11'],
    answerIndex: 2,
    correctAnswer: '10',
    explanation: '<p>You have 5 fingers on each hand. 5 + 5 = 10 fingers total!</p>',
    tags: ['counting', 'addition', 'real-world', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-21'),
    updatedAt: new Date('2024-01-21'),
    creatorName: 'Math Teacher',
  },
  {
    id: 'grade1-math-008',
    question: '<p>Which number is bigger: 6 or 4?</p>',
    options: ['4', '6', 'They are the same', 'I don\'t know'],
    answerIndex: 1,
    correctAnswer: '6',
    explanation: '<p>6 is bigger than 4. When we count: 1, 2, 3, 4, 5, 6 - we see 6 comes after 4!</p>',
    tags: ['number-comparison', 'greater-than', 'grade-1'],
    score: 1,
    duration: 30,
    createdAt: new Date('2024-01-22'),
    updatedAt: new Date('2024-01-22'),
    creatorName: 'Math Teacher',
  }
];

// Helper function to transform learning-service ItemDto to CMS AuthorItem
const transformItemDtoToAuthorItem = (itemDto: any): AuthorItem => {
  return {
    id: itemDto.id,
    question: itemDto.question,
    options: itemDto.options || [],
    correctAnswer: itemDto.correctAnswer,
    answerIndex: itemDto.answerIndex,
    explanation: itemDto.explanation,
    hints: itemDto.hints,
    example: itemDto.example,
    justification: itemDto.justification,
    creatorId: itemDto.creatorId,
    creatorName: itemDto.creatorName,
    cognitiveSkill: itemDto.cognitiveSkill,
    inUse: itemDto.inUse,
    tags: itemDto.tags || [],
    foundationTags: itemDto.foundationTags,
    groups: itemDto.groups,
    comments: itemDto.comments,
    createdAt: itemDto.createdAt ? new Date(itemDto.createdAt) : undefined,
    updatedAt: itemDto.updatedAt ? new Date(itemDto.updatedAt) : undefined,
    score: itemDto.score,
    duration: itemDto.duration,
    mediaUrl: itemDto.mediaUrl,
  };
};

// Helper function to transform CMS AuthorItem to learning-service ItemDto
const transformAuthorItemToItemDto = (authorItem: AuthorItem): any => {
  return {
    id: authorItem.id,
    question: authorItem.question,
    options: authorItem.options,
    correctAnswer: authorItem.correctAnswer || (authorItem.answerIndex !== undefined ? authorItem.options[authorItem.answerIndex] : ''),
    answerIndex: authorItem.answerIndex,
    explanation: authorItem.explanation,
    hints: authorItem.hints,
    example: authorItem.example,
    justification: authorItem.justification,
    creatorId: authorItem.creatorId,
    creatorName: authorItem.creatorName,
    cognitiveSkill: authorItem.cognitiveSkill,
    inUse: authorItem.inUse,
    tags: authorItem.tags,
    foundationTags: authorItem.foundationTags,
    groups: authorItem.groups,
    comments: authorItem.comments,
    score: authorItem.score,
    duration: authorItem.duration,
    mediaUrl: authorItem.mediaUrl,
  };
};

export const itemService = {
  /** Values already used at each tag position, for the per-tag dropdowns. */
  async getTagVocabulary(): Promise<string[][]> {
    const response = await itemApi.get('/items/tag-vocabulary');
    return response.data?.data ?? [];
  },

  async getItems(
    filters: ItemFilters,
    pagination: PaginationParams
  ): Promise<PaginatedResponse<ItemSummary>> {
    try {
      // Try learning-service first
      const response = await itemApi.get('/items');
      const items: AuthorItem[] = response.data.map(transformItemDtoToAuthorItem);

      // If learning-service returns empty, use mock data
      const sourceItems = items.length > 0 ? items : GRADE_1_MATH_QUESTIONS;

      // Apply client-side filtering
      let filteredItems = [...sourceItems];

      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredItems = filteredItems.filter(item =>
          item.question.toLowerCase().includes(searchLower) ||
          item.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      if (filters.tags?.length) {
        filteredItems = filteredItems.filter(item =>
          filters.tags!.some(filterTag => item.tags.includes(filterTag))
        );
      }

      // Convert to ItemSummary format
      const itemSummaries: ItemSummary[] = filteredItems.map(item => ({
        id: item.id!,
        questionPreview: item.question.replace(/<[^>]*>/g, '').substring(0, 100),
        subject: 'Math',
        grades: ['1'],
        bloomLevel: 'Apply',
        difficulty: item.score || 1,
        tags: item.tags,
        status: 'published',
        createdAt: item.createdAt || new Date(),
        updatedAt: item.updatedAt || new Date(),
        createdBy: item.creatorName || 'Math Teacher',
      }));

      // Apply pagination
      const startIndex = (pagination.page - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedItems = itemSummaries.slice(startIndex, endIndex);

      return {
        items: paginatedItems,
        totalCount: itemSummaries.length,
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: Math.ceil(itemSummaries.length / pagination.pageSize),
      };
    } catch (error) {
      console.warn('Learning service not available, using Grade 1 Math mock data:', error);

      // Fallback to Grade 1 Math mock questions
      let filteredItems = [...GRADE_1_MATH_QUESTIONS];

      // Apply filters to mock data
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        filteredItems = filteredItems.filter(item =>
          item.question.toLowerCase().includes(searchLower) ||
          item.tags.some(tag => tag.toLowerCase().includes(searchLower))
        );
      }

      if (filters.tags?.length) {
        filteredItems = filteredItems.filter(item =>
          filters.tags!.some(filterTag => item.tags.includes(filterTag))
        );
      }

      // Convert to ItemSummary format
      const itemSummaries: ItemSummary[] = filteredItems.map(item => ({
        id: item.id!,
        questionPreview: item.question.replace(/<[^>]*>/g, '').substring(0, 100),
        subject: 'Math',
        grades: ['1'],
        bloomLevel: 'Apply',
        difficulty: item.score || 1,
        tags: item.tags,
        status: 'published',
        createdAt: item.createdAt || new Date(),
        updatedAt: item.updatedAt || new Date(),
        createdBy: item.creatorName || 'Math Teacher',
      }));

      // Apply pagination
      const startIndex = (pagination.page - 1) * pagination.pageSize;
      const endIndex = startIndex + pagination.pageSize;
      const paginatedItems = itemSummaries.slice(startIndex, endIndex);

      return {
        items: paginatedItems,
        totalCount: itemSummaries.length,
        page: pagination.page,
        pageSize: pagination.pageSize,
        totalPages: Math.ceil(itemSummaries.length / pagination.pageSize),
      };
    }
  },

  async getItemById(id: string): Promise<AuthorItem> {
    try {
      const response = await itemApi.get(`/items/${id}`);
      return transformItemDtoToAuthorItem(response.data);
    } catch (error) {
      console.warn(`Learning service not available for item ${id}, checking mock data:`, error);

      // Fallback to mock data
      const mockItem = GRADE_1_MATH_QUESTIONS.find(item => item.id === id);
      if (mockItem) {
        return mockItem;
      }

      throw new Error(`Item with id ${id} not found`);
    }
  },

  async createItem(item: Omit<AuthorItem, 'id'>): Promise<AuthorItem> {
    try {
      const itemDto = transformAuthorItemToItemDto(item as AuthorItem);
      const response = await itemApi.post('/items', itemDto);
      return transformItemDtoToAuthorItem(response.data);
    } catch (error) {
      console.error('Failed to create item:', error);
      throw new Error('Failed to create item');
    }
  },

  async updateItem(id: string, item: Partial<AuthorItem>): Promise<AuthorItem> {
    try {
      const itemDto = transformAuthorItemToItemDto(item as AuthorItem);
      itemDto.id = id; // Ensure ID is set
      const response = await itemApi.put(`/items/${id}`, itemDto);
      return transformItemDtoToAuthorItem(response.data);
    } catch (error) {
      console.error(`Failed to update item ${id}:`, error);
      throw new Error(`Failed to update item ${id}`);
    }
  },

  async deleteItem(id: string): Promise<void> {
    await itemApi.delete(`/items/${id}`);
  },

  async duplicateItem(id: string): Promise<AuthorItem> {
    const response = await itemApi.post(`/items/${id}/duplicate`);
    return response.data;
  },

  async bulkUpdateItems(ids: string[], updates: Partial<AuthorItem>): Promise<void> {
    await itemApi.patch('/items/bulk', { ids, updates });
  },

  async bulkDeleteItems(ids: string[]): Promise<void> {
    await itemApi.delete('/items/bulk', { data: { ids } });
  },

  // Taxonomy-related endpoints
  async getSubjects(): Promise<string[]> {
    const response = await itemApi.get('/taxonomy/subjects');
    return response.data;
  },

  async getDomains(subject: string): Promise<string[]> {
    const response = await itemApi.get(`/taxonomy/subjects/${subject}/domains`);
    return response.data;
  },

  async getTopics(subject: string, domain: string): Promise<string[]> {
    const response = await itemApi.get(`/taxonomy/subjects/${subject}/domains/${domain}/topics`);
    return response.data;
  },

  async getTags(): Promise<string[]> {
    const response = await itemApi.get('/taxonomy/tags');
    return response.data;
  },
};
